/**
 * アプリの中での共有。
 *
 * Android の WebView には Web Share API（navigator.share）が無い。iOS の WKWebView も
 * ファイルの共有は安定しない。そこでアプリの中では、OS の共有シートを直接呼ぶ。
 * 画像は一度アプリのキャッシュに書き出してから渡す（共有シートはファイルの場所しか受け取れない）。
 *
 * 「画像保存」も共有シートに任せる（iOS は「画像を保存」、Android は「ドライブ」「フォト」などが並ぶ）。
 * WebView では <a download> が動かないため。
 */

import { isNativeApp } from './native';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** 文章とURLを共有シートへ。キャンセルも含め、出せなければ false。 */
export async function nativeShareText(data: { title?: string; text?: string; url?: string }): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { Share } = await import('@capacitor/share');
    await Share.share({ ...data, dialogTitle: '共有する' });
    return true;
  } catch {
    return false;
  }
}

/** ファイル（スコア画像・名場面の動画）を共有シートへ。 */
export async function nativeShareFile(
  blob: Blob,
  fileName: string,
  data: { title?: string; text?: string } = {},
): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ]);
    const written = await Filesystem.writeFile({
      path: `share/${fileName}`,
      data: await blobToBase64(blob),
      directory: Directory.Cache,
      recursive: true,
    });
    await Share.share({ ...data, files: [written.uri], dialogTitle: '共有する' });
    return true;
  } catch {
    return false;
  }
}
